import { useCallback, useState, useSyncExternalStore } from "react";
import type { Dispatch, SetStateAction } from "react";

/**
 * What is ticked on Prep, shared with Multi View (YC-12 / YC-23).
 *
 * Cooking now and On deck each keep their own ticks, as they always have, so
 * the selection lives in one slot per lane here and the bar reads the union.
 * It is a plain module store rather than a provider: the screen that opens
 * from Prep is a different route, and the ticks have to survive the trip and
 * nothing longer. `useMultiViewSelectionLifecycle` clears them once the user
 * is on neither page, so a later visit starts empty (product: not persisted).
 */

export type SelectionMap = Record<number, boolean>;

/** The two Prep lanes that can be ticked. */
export type SelectionSlot = "cookingNow" | "onDeck";

const SLOTS: SelectionSlot[] = ["cookingNow", "onDeck"];

let state: Record<SelectionSlot, SelectionMap> = { cookingNow: {}, onDeck: {} };
let snapshot: number[] = [];
const listeners = new Set<() => void>();

function idsOf(map: SelectionMap): number[] {
  return Object.entries(map)
    .filter(([, ticked]) => ticked)
    .map(([id]) => Number(id));
}

function recompute() {
  const next = [...new Set(SLOTS.flatMap((slot) => idsOf(state[slot])))].sort((a, b) => a - b);
  // A new array only when the ids changed, so subscribers do not re-render for nothing.
  if (next.length !== snapshot.length || next.some((id, i) => id !== snapshot[i])) snapshot = next;
}

function emit() {
  recompute();
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSelectedIds(): number[] {
  return snapshot;
}

function setSlot(slot: SelectionSlot, map: SelectionMap) {
  state = { ...state, [slot]: map };
  emit();
}

/** Takes one recipe out of every lane. */
export function deselectSession(id: number) {
  state = {
    cookingNow: { ...state.cookingNow, [id]: false },
    onDeck: { ...state.onDeck, [id]: false },
  };
  emit();
}

/** Puts a recipe into the selection (used by "Add recipes" on the screen). */
export function selectSession(id: number, lane: SelectionSlot = "cookingNow") {
  state = { ...state, [lane]: { ...state[lane], [id]: true } };
  emit();
}

export function clearMultiViewSelection() {
  state = { cookingNow: {}, onDeck: {} };
  emit();
}

/** Every ticked recipe, from both lanes, ordered by id. */
export function useSelectedSessionIds(): number[] {
  return useSyncExternalStore(subscribe, getSelectedIds, getSelectedIds);
}

/**
 * Drop-in for `useState<Record<number, boolean>>({})` in a lane. With `shared`
 * off (the service board) it is exactly that; on, the ticks go to the store.
 */
export function useSelectionSlot(
  slot: SelectionSlot,
  shared: boolean,
): [SelectionMap, Dispatch<SetStateAction<SelectionMap>>] {
  const [local, setLocal] = useState<SelectionMap>({});
  const stored = useSyncExternalStore(
    subscribe,
    () => state[slot],
    () => state[slot],
  );
  const setShared: Dispatch<SetStateAction<SelectionMap>> = useCallback(
    (action) => {
      // Read the store at call time so two updates in a row compose, as setState's do.
      const current = state[slot];
      setSlot(slot, typeof action === "function" ? action(current) : action);
    },
    [slot],
  );

  return shared ? [stored, setShared] : [local, setLocal];
}
